import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { ApiRequestService, FilterPipe, MAT_ICON, TOAST_STATE, ToastmessageService, TranslationPipe, TranslationService } from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { GetProductsResponse } from '../../products/product-list/get-products.response';
import { Material } from '../../products/product-list/material.model';
import { CategoryProduct } from '../../shared/models/category-product.model';
import { Category } from '../../shared/models/category.model';
import { UpdateSiteConfigurationUseCase } from '../../use-cases/updatesiteconfiguration/update-site-configuration.usecase';
import { CategoryWithProductMappings } from '../updatecategory/model/category-with-product-mappings.model';
import { AddCategoryRequest } from './model/add-category.request';

@Component({
    selector: 'app-addcategory',
    templateUrl: './addcategory.component.html',
    styleUrls: ['./addcategory.component.scss'],
    standalone: true,
    imports: [MatIconModule, RouterModule, FormsModule, ReactiveFormsModule, FilterPipe, CommonModule, DragDropModule, TranslationPipe, MatCheckboxModule]
})
export class AddCategoryComponent implements OnInit {
    category: Category = {};
    categoryProductList: Material[] = [];
    filteredBooks: Material[] = [];
    offerLinesActive = false;
    offerLinesValue = '';
    products: Material[] = [];
    searchText = '';

    private _addCategoryUrl = '/admin/addcategory';
    private _categoryForm?: FormGroup;
    private _getProductsUrl = '/store/get-products';
    private _storeBaseUrl = inject('STORE_BACKEND_URL' as any) as string;
    private _apiRequestService = inject(ApiRequestService);
    private _breakpointObserver = inject(BreakpointObserver);
    private _fb = inject(FormBuilder);
    private _router = inject(Router);
    private _toastMessageService = inject(ToastmessageService);
    private _translateService = inject(TranslationService);
    private _updateSiteConfigUseCase = inject(UpdateSiteConfigurationUseCase);

    isExtraSmall: Observable<BreakpointState> = this._breakpointObserver.observe(Breakpoints.XSmall);
    isMedium: Observable<BreakpointState> = this._breakpointObserver.observe(Breakpoints.Medium);
    isSmall: Observable<BreakpointState> = this._breakpointObserver.observe(Breakpoints.Small);

    get categoryForm(): FormGroup | undefined {
        return this._categoryForm;
    }

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            this._router.navigateByUrl('/');
        }
        if (sessionStorage.getItem('product')) {
            const product = JSON.parse(sessionStorage.getItem('product') as string) as Material;
            const index = this.products.indexOf(product);
            this.products.splice(index, 1);
            this.categoryProductList.push(product);
            sessionStorage.removeItem('product');
        }
        this._createCategoryForm();
        this._apiRequestService.sendRequest(this._storeBaseUrl, {}, true, this._getProductsUrl).subscribe((response) => {
            const products = response as GetProductsResponse;
            this.products = products.productList as Material[];
            this.filteredBooks = this.products;
        });
    }

    private _createCategoryForm(): void {
        this._categoryForm = this._fb.group({
            name: ['', [Validators.required]]
        });
    }

    addCategory(category: Category): void {
        if (this.categoryProductList.length === 0) {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                'Kérem adjon hozzá könyveket a csomaghoz!!',
                MAT_ICON.warning
            );
            this._dismissError();
        } else {
            this._collectCategoryData();

            const categoryProductList: CategoryProduct[] = this.categoryProductList.map((product, index) => {
                const categoryProduct = new CategoryProduct();
                categoryProduct.product = product;
                categoryProduct.category = { ...this.category };
                categoryProduct.orderNumber = index;
                return categoryProduct;
            });

            const categoryWithProductMappings: CategoryWithProductMappings = {
                category: category,
                categoryProducts: categoryProductList
            };

            const addCategoryRequest: AddCategoryRequest = {
                categoryWithProductMappings: categoryWithProductMappings
            };

            this._apiRequestService
                .sendRequest<
                    AddCategoryRequest,
                    HttpResponse<any>
                >(this._storeBaseUrl, addCategoryRequest, true, this._addCategoryUrl, true)
                .subscribe(() => {
                    this._router.navigateByUrl('/categorys');
                });
        }
    }

    private _dismissError(): void {
        setTimeout(() => {
            this._toastMessageService.dismissToast();
        }, 3000);
    }

    private _collectCategoryData(): void {
        this.category = {
            name: this._categoryForm?.controls['name'].value as string | undefined
        };
    }

    addProductToCategory(product: Material) {
        const index = this.products.indexOf(product);
        this.products.splice(index, 1);
        this.categoryProductList.push(product);
    }

    deleteFromCategoryProductList(product: Material) {
        const index = this.categoryProductList.indexOf(product);
        this.categoryProductList.splice(index, 1);
        this.products.push(product);
    }

    drop(event: CdkDragDrop<CategoryProduct[]>): void {
        moveItemInArray(this.categoryProductList, event.previousIndex, event.currentIndex);
    }

    onChangeOfferLinesActive(): void {
        if (!this.offerLinesValue.trim()) {
            this.offerLinesActive = false;
            return;
        }
        this.offerLinesActive = !this.offerLinesActive;
    }

    saveOfferLines(): void {
        if (!this.category.id) {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                this._translateService.translate('Először mentse el a kategóriát!'),
                MAT_ICON.warning
            );
            this._dismissError();
            return;
        }
        const key = 'category-' + this.category.id + '-lines';
        const result = this._updateSiteConfigUseCase.execute({
            configurationKey: key,
            configurationValue: this.offerLinesValue,
            active: this.offerLinesActive
        });
        if (result) {
            (result as Observable<any>).subscribe(() => { });
        }
    }
}
